import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Modal,
  ParagraphText,
  StackLayout,
  VisuallyHidden,
} from "@leagueplatform/genesis-core";
import successCheck from "assets/success-check.svg";
import { useIntl } from "@leagueplatform/locales";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { BaseModalContainer } from "components/modals/common/base-modal.component";
import { ImageMessageModalContent } from "components/modals/common/image-message-modal-content.component";
import {
  closeUploadPhysicianFormModal,
  useUploadPhysicianFormModalOpen,
} from "utils/upload-physician-form/modal-store";
import {
  PHYSICIAN_FORM_ACCEPTED_EXTENSIONS,
  validatePhysicianFormFile,
  type PhysicianFormFileError,
} from "utils/upload-physician-form/validate-file";
import { uploadPhysicianForm } from "utils/upload-physician-form/upload";

type UploadError = PhysicianFormFileError | "upload";

const ERROR_MESSAGE_KEYS = {
  fileSize: "STR_UPLOAD_FORM_FILE_TOO_LARGE",
  fileType: "STR_UPLOAD_FORM_INVALID_FILE_TYPE",
  upload: "STR_UPLOAD_FORM_UPLOAD_ERROR",
} as const satisfies Record<UploadError, string>;

export const UploadPhysicianFormModal = () => {
  const isOpen = useUploadPhysicianFormModalOpen();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const removeButtonRef = useRef<HTMLButtonElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<UploadError | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => closeUploadPhysicianFormModal, []);

  useEffect(() => {
    if (file) removeButtonRef.current?.focus();
  }, [file]);

  const resetAndClose = () => {
    if (isUploading) return;
    const shouldRedirectToScreenings = isSuccess;
    setFile(null);
    setError(null);
    setIsSuccess(false);
    setIsDragging(false);
    closeUploadPhysicianFormModal();
    if (shouldRedirectToScreenings) {
      navigate(APP_MANAGED_ROUTES.SCREENINGS);
    }
  };

  const handleSelect = (candidate: File | undefined) => {
    if (!candidate) return;
    const validationError = validatePhysicianFormFile(candidate);
    setFile(validationError ? null : candidate);
    setError(validationError);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleSelect(event.target.files?.[0]);
    // Clear the input so re-selecting the same file after a remove fires
    // another change event.
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    handleSelect(event.dataTransfer.files?.[0]);
  };

  const handleSubmit = async () => {
    if (!file || isUploading) return;
    setIsUploading(true);
    setError(null);
    try {
      await uploadPhysicianForm(file);
      setIsSuccess(true);
    } catch {
      setError("upload");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) resetAndClose();
      }}
    >
      {isSuccess ? (
        <ImageMessageModalContent
          image={successCheck}
          title={formatMessage({ id: "STR_UPLOAD_FORM_SUCCESS_TITLE" })}
          centerTextOnMobile
          fullHeightOnMobile
          autoFocusTitle
          footer={
            <Button
              onClick={resetAndClose}
              css={{
                alignSelf: "flex-end",
                justifyContent: "center",
                "@mobile": { alignSelf: "stretch", width: "100%" },
              }}
            >
              {formatMessage({ id: "STR_CLOSE" })}
            </Button>
          }
        >
          <ParagraphText>
            {formatMessage({ id: "STR_UPLOAD_FORM_SUCCESS_BODY" })}
          </ParagraphText>
        </ImageMessageModalContent>
      ) : (
        <BaseModalContainer
          title={formatMessage({ id: "STR_UPLOAD_FORM_MODAL_TITLE" })}
          footer={
            <Button
              width="fillContainer"
              onClick={handleSubmit}
              disabled={!file || isUploading}
            >
              {formatMessage({
                id: isUploading
                  ? "STR_UPLOAD_FORM_UPLOADING"
                  : "STR_UPLOAD_FORM_SUBMIT",
              })}
            </Button>
          }
        >
          <StackLayout spacing="$one" horizontalAlignment="stretch">
            <ParagraphText>
              {formatMessage({ id: "STR_UPLOAD_FORM_REQUIREMENTS" })}
            </ParagraphText>
            <Box
              onDragOver={(event: React.DragEvent) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              css={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "$half",
                padding: "$oneAndHalf",
                borderWidth: "$thin",
                borderStyle: "dashed",
                borderColor: isDragging
                  ? "$interactiveActionPrimary"
                  : "$onSurfaceBorderSubdued",
                borderRadius: "$medium",
              }}
            >
              {file ? (
                <>
                  <ParagraphText
                    css={{
                      fontWeight: "bold",
                      overflowWrap: "anywhere",
                      textAlign: "center",
                    }}
                  >
                    {file.name}
                  </ParagraphText>
                  <Button
                    ref={removeButtonRef}
                    priority="tertiary"
                    quiet
                    size="small"
                    icon="tinyClose"
                    onClick={() => setFile(null)}
                  >
                    {formatMessage({ id: "STR_UPLOAD_FORM_REMOVE_FILE" })}
                  </Button>
                </>
              ) : (
                <>
                  <ParagraphText>
                    {formatMessage({ id: "STR_UPLOAD_FORM_DROPZONE_HINT" })}
                  </ParagraphText>
                  <Button
                    priority="secondary"
                    onClick={() => inputRef.current?.click()}
                  >
                    {formatMessage({ id: "STR_UPLOAD_FORM_BROWSE" })}
                  </Button>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                accept={PHYSICIAN_FORM_ACCEPTED_EXTENSIONS.join(",")}
                onChange={handleInputChange}
                aria-label={formatMessage({ id: "STR_UPLOAD_FORM_BROWSE" })}
                style={{ display: "none" }}
              />
            </Box>
            {error && (
              <ParagraphText
                role="alert"
                css={{ color: "$onSurfaceTextCritical" }}
              >
                {formatMessage({ id: ERROR_MESSAGE_KEYS[error] })}
              </ParagraphText>
            )}
            <VisuallyHidden role="status">
              {isUploading
                ? formatMessage({ id: "STR_UPLOAD_FORM_UPLOADING" })
                : null}
            </VisuallyHidden>
          </StackLayout>
        </BaseModalContainer>
      )}
    </Modal.Root>
  );
};
